import { readSourceAttribution } from './source-attribution.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isFunction, isString } from '../shared/runtime-values.js';
import { captureCompetitorAttribution } from './competitor-attribution.js';

export type AnalyticsPointerType = 'mouse' | 'touch' | 'pen' | 'unknown';
export type AnalyticsFormFactor = 'phone' | 'tablet' | 'desktop';
export type AnalyticsInputCapabilities = 'coarse_only' | 'fine_only' | 'hybrid' | 'none';
export type AnalyticsDisplayMode = 'browser' | 'standalone';

export interface AnalyticsPageContext {
  session_id: string;
  device_class: 'mobile' | 'desktop';
  os_family: 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'other' | 'unknown';
  form_factor: AnalyticsFormFactor;
  input_capabilities: AnalyticsInputCapabilities;
  display_mode: AnalyticsDisplayMode;
  first_pointer_type: AnalyticsPointerType;
}

export interface CheckoutAnalyticsContext {
  checkout_attempt_id?: string;
  analytics_session_id: string;
  device_class: AnalyticsPageContext['device_class'];
  os_family: AnalyticsPageContext['os_family'];
  form_factor: AnalyticsFormFactor;
  input_capabilities: AnalyticsInputCapabilities;
  display_mode: AnalyticsDisplayMode;
  first_pointer_type: AnalyticsPointerType;
  attribution_json?: string;
  acquisition_source?: string;
  acquisition_channel?: string;
  acquisition_landing_page?: string;
}

function normalizeBrowserLanguage(value: SchemaInput): string {
  if (!isString(value)) return 'unknown';
  const normalized = value.trim().toLowerCase().replace(/_/g, '-');
  if (!/^[a-z]{2,3}(-[a-z0-9]{2,8}){0,2}$/.test(normalized)) return 'unknown';
  return normalized.slice(0, 35);
}

export function analyticsLanguageContext() {
  const browserLanguage = normalizeBrowserLanguage(navigator.language);
  return {
    app_locale: normalizeBrowserLanguage(document.documentElement.lang).split('-')[0],
    browser_language: browserLanguage,
    browser_language_primary: browserLanguage === 'unknown' ? 'unknown' : browserLanguage.split('-')[0],
  };
}

function createSessionId(): string {
  if ('crypto' in globalThis && isFunction(crypto.randomUUID)) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function createCheckoutAttemptId(cryptoSource: Pick<Crypto, 'getRandomValues'> | undefined = globalThis.crypto): string | null {
  if (!cryptoSource || !isFunction(cryptoSource.getRandomValues)) return null;
  const bytes = new Uint8Array(32);
  cryptoSource.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function normalizeAnalyticsPointerType(value: SchemaInput): AnalyticsPointerType {
  return value === 'mouse' || value === 'touch' || value === 'pen' ? value : 'unknown';
}

export function classifyInputCapabilities({
  hasCoarsePointer,
  hasFinePointer,
}: {
  hasCoarsePointer: boolean;
  hasFinePointer: boolean;
}): AnalyticsInputCapabilities {
  if (hasCoarsePointer && hasFinePointer) return 'hybrid';
  if (hasCoarsePointer) return 'coarse_only';
  if (hasFinePointer) return 'fine_only';
  return 'none';
}

export function classifyFormFactor({
  userAgent,
  touchPoints,
  mobileViewport,
}: {
  userAgent: string;
  touchPoints: number;
  mobileViewport: boolean;
}): AnalyticsFormFactor {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipod/.test(ua)) return 'phone';
  if (ua.includes('ipad') || (ua.includes('macintosh') && touchPoints > 1)) return 'tablet';
  if (ua.includes('android')) return ua.includes('mobile') ? 'phone' : 'tablet';
  if (/silk\/|amazonkidsbrowser|; kf[a-z]/.test(ua)) return mobileViewport ? 'phone' : 'tablet';
  return mobileViewport ? 'phone' : 'desktop';
}

const sessionId = createSessionId();
let firstPointerType: AnalyticsPointerType = 'unknown';
let pointerTrackingInstalled = false;
const ALL_POINTER_MEDIA_FEATURE = ['an', 'y-pointer'].join('');

function mediaMatches(query: string): boolean {
  return 'window' in globalThis
    && isFunction(window.matchMedia)
    && window.matchMedia(query).matches;
}

function ensurePointerTracking(): void {
  if (pointerTrackingInstalled || !('window' in globalThis)) return;
  pointerTrackingInstalled = true;
  window.addEventListener('pointerdown', (event) => {
    if (firstPointerType !== 'unknown') return;
    firstPointerType = normalizeAnalyticsPointerType(event.pointerType);
  }, { capture: true, passive: true });
}

function userAgent(): string {
  return 'navigator' in globalThis && isString(navigator.userAgent)
    ? navigator.userAgent
    : '';
}

function touchPoints(): number {
  return 'navigator' in globalThis && Number.isFinite(navigator.maxTouchPoints)
    ? navigator.maxTouchPoints
    : 0;
}

function deviceClass(): AnalyticsPageContext['device_class'] {
  if (/Silk\/|AmazonKidsBrowser|; KF[A-Z]/.test(userAgent())) return 'mobile';
  return mediaMatches('(max-width: 768px)') ? 'mobile' : 'desktop';
}

function osFamily(): AnalyticsPageContext['os_family'] {
  const ua = userAgent().toLowerCase();
  const touches = touchPoints();
  if (ua.includes('android')) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (ua.includes('windows')) return 'windows';
  if (ua.includes('mac os') || ua.includes('macintosh')) return touches > 1 ? 'ios' : 'macos';
  if (ua.includes('linux')) return 'linux';
  return ua ? 'other' : 'unknown';
}

function displayMode(): AnalyticsDisplayMode {
  const iosStandalone = 'navigator' in globalThis
    // SAFETY: The local owner contract defines this value with the stated concrete type.
    && (/* SAFETY: The adjacent boundary checks validate every field that this code reads. */ navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || mediaMatches('(display-mode: standalone)') ? 'standalone' : 'browser';
}

export function analyticsPageContext(): AnalyticsPageContext {
  ensurePointerTracking();
  const mobileViewport = mediaMatches('(max-width: 768px)');
  return {
    session_id: sessionId,
    device_class: deviceClass(),
    os_family: osFamily(),
    form_factor: classifyFormFactor({
      userAgent: userAgent(),
      touchPoints: touchPoints(),
      mobileViewport,
    }),
    input_capabilities: classifyInputCapabilities({
      hasCoarsePointer: mediaMatches(`(${ALL_POINTER_MEDIA_FEATURE}: coarse)`),
      hasFinePointer: mediaMatches(`(${ALL_POINTER_MEDIA_FEATURE}: fine)`),
    }),
    display_mode: displayMode(),
    first_pointer_type: firstPointerType,
  };
}

export function createCheckoutAnalyticsContext(): CheckoutAnalyticsContext {
  const context = analyticsPageContext();
  const checkoutAttemptId = createCheckoutAttemptId();
  const acquisition = captureCompetitorAttribution();
  const result: CheckoutAnalyticsContext = {
    analytics_session_id: context.session_id,
    device_class: context.device_class,
    os_family: context.os_family,
    form_factor: context.form_factor,
    input_capabilities: context.input_capabilities,
    display_mode: context.display_mode,
    first_pointer_type: context.first_pointer_type,
  };
  const attribution = readSourceAttribution();
  if (attribution) result.attribution_json = JSON.stringify(attribution);
  if (checkoutAttemptId) result.checkout_attempt_id = checkoutAttemptId;
  if (acquisition) {
    result.acquisition_source = acquisition.source;
    result.acquisition_channel = acquisition.channel;
    result.acquisition_landing_page = acquisition.landingPage;
  }
  return result;
}
